import { Icon, type IconProps } from '@chakra-ui/react';
import type { ComponentType } from 'react';
import { GoogleLogo } from './GoogleLogo';
import { MicrosoftLogo } from './MicrosoftLogo';

/**
 * Product marks used to identify connector providers and their capabilities.
 *
 * Keep vendor artwork here rather than coupling presentation to Composio's
 * catalog responses. The fixed colours are mandated product-brand colours and
 * are the sanctioned exception to semantic colour tokens (brand marks only).
 */

function GoogleDriveIcon(props: IconProps) {
  return (
    <Icon viewBox="0 0 512 512" {...props}>
      <path
        fill="#0066DA"
        d="m38.7 419.3 22.6 39c4.7 8.2 11.4 14.7 19.4 19.4l80.6-139.6H0c0 9.1 2.3 18.2 7 26.4z"
      />
      <path
        fill="#00AC47"
        d="M256 173.9 175.4 34.3c-7.9 4.7-14.7 11.1-19.4 19.4L7 311.7c-4.6 8-7 17.1-7 26.4h161.3z"
      />
      <path
        fill="#EA4335"
        d="M431.4 477.7c7.9-4.7 14.7-11.1 19.4-19.4l9.4-16.1 44.9-77.7c4.7-8.2 7-17.3 7-26.4H350.7l34.3 67.4z"
      />
      <path
        fill="#00832D"
        d="m256 173.9 80.6-139.6c-7.9-4.7-17-7-26.4-7H201.8c-9.4 0-18.5 2.6-26.4 7z"
      />
      <path
        fill="#2684FC"
        d="M350.7 338.1H161.3L80.6 477.7c7.9 4.7 17 7 26.4 7h298c9.4 0 18.5-2.6 26.4-7z"
      />
      <path
        fill="#FFBA00"
        d="M430.5 182.7 356 53.7c-4.7-8.2-11.4-14.7-19.4-19.4L256 173.9l94.7 164.2h161c0-9.1-2.3-18.2-7-26.4z"
      />
    </Icon>
  );
}

function GoogleDocsIcon(props: IconProps) {
  return (
    <Icon viewBox="0 0 512 512" {...props}>
      <path
        fill="#3086F6"
        d="M407.3 512H104.7c-19.2 0-34.9-15.7-34.9-34.9V34.9C69.8 15.7 85.5 0 104.7 0h209.5l128 128v349.1c0 19.2-15.7 34.9-34.9 34.9"
      />
      <path fill="#0C67D6" d="m314.2 0 128 128h-128z" />
      <path
        fill="#FDFFFF"
        d="M360.7 226.9H151.3v-29.1h209.5v29.1zm0 40.7H151.3v29.1h209.5v-29.1zm-58.2 69.9H151.3v29.1h151.3v-29.1z"
      />
    </Icon>
  );
}

function GoogleSheetsIcon(props: IconProps) {
  return (
    <Icon viewBox="0 0 512 512" {...props}>
      <path fill="#188038" d="M442.2 128 314.2 0v128z" />
      <path
        fill="#34A853"
        d="M314.2 128V0H104.7C85.4 0 69.8 15.6 69.8 34.9v442.2c0 19.3 15.6 34.9 34.9 34.9h302.5c19.3 0 34.9-15.6 34.9-34.9V128z"
      />
      <path
        fill="#FFF"
        d="M139.6 197.8v168.7h232.7V197.8zm101.9 139.7h-72.7v-40.7h72.7zm0-69.9h-72.7v-40.7h72.7zm101.8 69.9h-72.7v-40.7h72.7zm0-69.9h-72.7v-40.7h72.7z"
      />
    </Icon>
  );
}

function GoogleSlidesIcon(props: IconProps) {
  return (
    <Icon viewBox="0 0 512 512" {...props}>
      <path fill="#F29900" d="M442.2 128 314.2 0v128z" />
      <path
        fill="#FBBC04"
        d="M314.2 128V0H104.7C85.4 0 69.8 15.6 69.8 34.9v442.2c0 19.3 15.6 34.9 34.9 34.9h302.5c19.3 0 34.9-15.6 34.9-34.9V128z"
      />
      <path fill="#FFF" d="M139.6 197.8v157.1h232.7V197.8zm203.7 128H168.7v-98.9h174.5v98.9z" />
    </Icon>
  );
}

function MicrosoftOneDriveIcon(props: IconProps) {
  return (
    <Icon viewBox="0 0 512 512" {...props}>
      <path
        fill="#0364B8"
        d="m195.2 183.1 107.5 64.4 64-27c13-5.6 27-8.5 41.2-8.5 2.4 0 4.7.1 7 .3-21.8-85.6-109-137.2-194.6-115.3-38.5 9.9-72 33.8-93.9 67h1.6c23.7 0 47 6.6 67.2 19.1"
      />
      <path
        fill="#0078D4"
        d="M195.2 183.1C175 170.6 151.7 164 128 164h-1.6C55.7 164.9-.9 222.9 0 293.6c.3 25.6 8.3 50.6 23 71.6l94.8-39.9 42.1-17.7 93.8-39.5 49-20.6z"
      />
      <path
        fill="#1490DF"
        d="M415 212.3c-2.3-.2-4.7-.3-7-.3-14.2 0-28.2 2.9-41.2 8.5l-64 27 18.6 11.1 60.9 36.5 26.6 15.9 90.8 54.4c27.2-50.6 8.3-113.6-42.2-140.9-13.3-7.1-27.7-11.2-42.5-12.2"
      />
      <path
        fill="#28A8EA"
        d="m408.7 311-26.6-15.9-60.9-36.5-18.6-11.1-49 20.6-93.8 39.5-42.1 17.7L23 365.2C46.9 399.6 86.1 420 128 420h280c38.2 0 73.4-21 91.6-54.6z"
      />
    </Icon>
  );
}

function MicrosoftSharePointIcon(props: IconProps) {
  return (
    <Icon viewBox="0 0 512 512" {...props}>
      <circle cx="262" cy="148.8" r="142.9" fill="#036C70" />
      <circle cx="381" cy="279.8" r="131" fill="#1A9BA1" />
      <circle cx="279.8" cy="404.8" r="101.2" fill="#37C6D0" />
      <path
        d="M285.8 134.9V389c-.1 8.8-5.4 16.8-13.6 20.1-2.6 1.1-5.4 1.7-8.2 1.7h-85.3c-.1-2-.1-3.9-.1-6 0-2 0-4 .2-6 2.2-38.1 25.6-71.6 60.6-86.8v-22.1c-77.8-12.3-131-85.4-118.6-163.3.1-.5.2-1.1.3-1.6.6-4 1.4-8 2.5-11.9H264c12 .1 21.7 9.8 21.8 21.8"
        opacity=".1"
      />
      <path
        d="M252.1 125h-131c-13.2 77.7 39 151.4 116.7 164.7 2.4.4 4.7.7 7.1 1-36.9 17.5-63.8 67.2-66.1 108.2-.2 2-.3 4-.2 6 0 2 0 3.9.1 6 .2 4 .7 8 1.5 11.9H252c8.8-.1 16.8-5.4 20.1-13.6 1.1-2.6 1.7-5.4 1.7-8.2V146.8c0-12-9.7-21.7-21.7-21.8"
        opacity=".2"
      />
      <path
        fill="#038489"
        d="M21.8 125h218.3c12.1 0 21.8 9.8 21.8 21.8v218.3c0 12.1-9.8 21.8-21.8 21.8H21.8C9.8 387 0 377.2 0 365.2V146.8c0-12 9.8-21.8 21.8-21.8"
      />
      <path
        fill="#FFF"
        d="M97.5 253.3c-5.1-3.4-9.4-7.9-12.5-13.2-3-5.5-4.5-11.8-4.3-18.1-.3-8.5 2.6-16.9 8.1-23.4 5.8-6.6 13.2-11.4 21.5-14 9.5-3.1 19.4-4.7 29.4-4.5 13.1-.5 26.2 1.4 38.7 5.4v27.4c-5.4-3.3-11.3-5.7-17.5-7.1-6.7-1.6-13.6-2.5-20.5-2.5-7.3-.3-14.5 1.3-21 4.5-5 2.2-8.3 7.1-8.3 12.6 0 3.3 1.3 6.5 3.6 9 2.7 2.8 6 5.1 9.5 6.8 4 2 9.9 4.6 17.9 7.9.9.3 1.7.6 2.5 1 7.8 3.1 15.3 6.8 22.5 11.1 5.4 3.4 10 7.9 13.3 13.4 3.4 6.2 5 13.2 4.8 20.2.4 8.8-2.3 17.4-7.6 24.4-5.3 6.4-12.3 11.2-20.2 13.6-9.3 2.9-19.1 4.3-28.9 4.2-8.8 0-17.5-.7-26.2-2.1-7.3-1.2-14.4-3.3-21.2-6.2v-28.9c6.5 4.6 13.7 8.1 21.4 10.2 7.7 2.4 15.6 3.7 23.6 3.8 7.4.5 14.8-1.1 21.4-4.5 4.6-2.6 7.4-7.5 7.3-12.8 0-3.7-1.4-7.2-4-9.8-3.3-3.2-7-5.8-11.2-7.8-4.8-2.4-11.8-5.5-21-9.4-7.4-3.1-14.5-6.8-21.1-11.2"
      />
    </Icon>
  );
}

function MicrosoftWordIcon(props: IconProps) {
  return (
    <Icon viewBox="0 0 512 512" {...props}>
      <path
        fill="#41A5EE"
        d="M490.2 17.9H140.9c-12.1 0-21.8 9.8-21.8 21.8v97.2l202.4 59.5L512 136.9V39.7c0-12.1-9.8-21.8-21.8-21.8"
      />
      <path fill="#2B7CD3" d="M512 136.9H119.1V256l202.4 35.7L512 256z" />
      <path fill="#185ABD" d="M119.1 256v119.1l190.5 23.8L512 375.1V256z" />
      <path
        fill="#103F91"
        d="M140.9 494.1h349.3c12.1 0 21.8-9.8 21.8-21.8v-97.2H119.1v97.2c0 12.1 9.7 21.8 21.8 21.8"
      />
      <path
        fill="#185ABD"
        d="M21.8 125h218.3c12.1 0 21.8 9.8 21.8 21.8v218.3c0 12.1-9.8 21.8-21.8 21.8H21.8C9.8 387 0 377.2 0 365.2V146.8c0-12 9.8-21.8 21.8-21.8"
      />
      <path
        fill="#FFF"
        d="M89.6 292.4c.4 3.4.7 6.3.8 8.8h.5c.2-2.4.6-5.3 1.2-8.6.6-3.4 1.1-6.2 1.6-8.5l23-99h29.7l23.8 97.6c1.4 6.1 2.4 12.2 3 18.4h.4c.4-6 1.3-12 2.5-17.9l19-98.1h27L188.7 327h-31.6l-22.6-94c-.7-2.7-1.4-6.2-2.2-10.6-.8-4.3-1.3-7.5-1.5-9.5h-.4c-.3 2.3-.8 5.7-1.5 10.3-.8 4.5-1.4 7.9-1.8 10.1L105.8 327H73.6L40 185.1h27.5l20.7 99.2c.5 2 .9 4.8 1.4 8.1"
      />
    </Icon>
  );
}

function MicrosoftExcelIcon(props: IconProps) {
  return (
    <Icon viewBox="0 0 512 512" {...props}>
      <path
        fill="#185C37"
        d="m321.5 244.1-202.4-35.7v263.9c0 12.1 9.8 21.8 21.8 21.8h349.3c12.1 0 21.8-9.8 21.8-21.8v-97.2z"
      />
      <path
        fill="#21A366"
        d="M321.5 17.9H140.9c-12.1 0-21.8 9.8-21.8 21.8v97.2L321.5 256l107.2 35.7L512 256V136.9z"
      />
      <path
        fill="#107C41"
        d="M119.1 136.9h202.4V256H119.1zM21.8 125h218.3c12.1 0 21.8 9.8 21.8 21.8v218.3c0 12.1-9.8 21.8-21.8 21.8H21.8C9.8 387 0 377.2 0 365.2V146.8c0-12 9.8-21.8 21.8-21.8"
      />
      <path
        fill="#FFF"
        d="m67.6 326.9 45.9-71.1L71.4 185h33.8l23 45.2c2.1 4.3 3.6 7.5 4.4 9.6h.3c1.5-3.4 3.1-6.8 4.8-10l24.5-44.8h31.1l-43.1 70.3 44.2 71.5h-33.1l-26.5-49.7c-1.2-2.1-2.3-4.3-3.2-6.6h-.4c-.8 2.3-1.8 4.4-3.1 6.4l-27.3 49.9z"
      />
      <path fill="#33C481" d="M490.2 17.9H321.5V137H512V39.7c0-12.1-9.8-21.8-21.8-21.8" />
      <path fill="#107C41" d="M321.5 256H512v119.1H321.5z" />
    </Icon>
  );
}

function MicrosoftOneNoteIcon(props: IconProps) {
  return (
    <Icon viewBox="0 0 512 512" {...props}>
      <path
        fill="#CA64EA"
        d="M392.9 494.1h-252c-12 0-21.8-9.8-21.8-21.8V39.7c0-12 9.8-21.8 21.8-21.8h349.3c12 0 21.8 9.8 21.8 21.8V137z"
      />
      <path fill="#AE4BD5" d="m512 256-59.5 35.7-59.6-35.7V136.9H512z" />
      <path fill="#9332BF" d="m512 375.1-59.5 35.7-59.5-35.7V256h119z" />
      <path
        fill="#7719AA"
        d="M392.9 375.1H512v96.7c0 12.3-10 22.3-22.3 22.3H393v-119zM21.8 125h218.3c12.1 0 21.8 9.8 21.8 21.8v218.3c0 12.1-9.8 21.8-21.8 21.8H21.8C9.8 387 0 377.2 0 365.2V146.8c0-12 9.8-21.8 21.8-21.8"
      />
      <path
        fill="#FFF"
        d="M71.8 185.1h29.6l58.1 94.1q3.9 6.3 6 10.2h.4c-.7-6.4-1-12.8-.9-19.3v-85h25.2V327h-27.7l-60.3-96.7c-2-3.2-3.8-6.5-5.4-10h-.5c.6 7 .9 14.1.8 21.2V327H71.8z"
      />
    </Icon>
  );
}

const CONNECTOR_ICONS = {
  google: GoogleLogo,
  microsoft: MicrosoftLogo,
  'google-drive': GoogleDriveIcon,
  'google-docs': GoogleDocsIcon,
  'google-sheets': GoogleSheetsIcon,
  'google-slides': GoogleSlidesIcon,
  'microsoft-onedrive': MicrosoftOneDriveIcon,
  'microsoft-sharepoint': MicrosoftSharePointIcon,
  'microsoft-word': MicrosoftWordIcon,
  'microsoft-excel': MicrosoftExcelIcon,
  'microsoft-onenote': MicrosoftOneNoteIcon,
} as const satisfies Record<string, ComponentType<IconProps>>;

export type ConnectorBrandIconName = keyof typeof CONNECTOR_ICONS;

interface ConnectorBrandIconProps extends IconProps {
  readonly name: ConnectorBrandIconName;
  readonly label?: string;
}

export function ConnectorBrandIcon({ name, label, ...props }: ConnectorBrandIconProps) {
  const BrandIcon = CONNECTOR_ICONS[name];
  return (
    <BrandIcon
      aria-hidden={label ? undefined : true}
      aria-label={label}
      data-connector-icon={name}
      {...props}
    />
  );
}
